import React, { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { lowlight } from "lowlight";
import FeatherIcon from "feather-icons-react";
import ButtonNode from "./ButtonNode";
import "./blog-editor.css";

const ToolbarButton = ({ onClick, active, disabled, title, icon, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={"cms-editor-toolbar-btn" + (active ? " is-active" : "")}
  >
    {icon ? <FeatherIcon icon={icon} size={15} /> : children}
  </button>
);

// Rich text editor for blog post bodies. Emits sanitized-on-save HTML (the
// backend re-sanitizes on every write — this is just the authoring surface)
// via onChange(html), matching the app's usual controlled-field convention.
const BlogEditor = ({ value, onChange, onUploadImage }) => {
  const imageInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      CodeBlockLowlight.configure({ lowlight }),
      ButtonNode,
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The modal (and this editor) stays mounted for the whole page's lifetime and gets
  // reused across "ADD", "EDIT post A", "EDIT post B" — TipTap's `content` option only
  // applies once, at creation, so external resets (opening a different post, clearing
  // the form) have to be pushed in explicitly. Guarded by an equality check so this
  // doesn't fight the user's own typing (onUpdate above also changes `value`).
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (editor.getHTML() !== incoming) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertButton = useCallback(() => {
    if (!editor) return;
    const label = window.prompt("Button label", "Learn more");
    if (!label) return;
    const href = window.prompt("Button link URL", "https://");
    if (!href) return;
    editor.chain().focus().insertContent({ type: "ctaButton", attrs: { label, href } }).run();
  }, [editor]);

  const handleImagePick = useCallback(() => {
    imageInputRef.current && imageInputRef.current.click();
  }, []);

  const handleImageFile = useCallback(
    async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file || !editor || !onUploadImage) return;

      const url = await onUploadImage(file);
      if (url) {
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      }
    },
    [editor, onUploadImage]
  );

  if (!editor) return null;

  return (
    <div className="cms-editor">
      <div className="cms-editor-toolbar">
        <ToolbarButton
          title="Heading 1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          title="Heading 2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title="Heading 3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>
        <span className="cms-editor-divider" />
        <ToolbarButton
          title="Bold"
          icon="bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          title="Italic"
          icon="italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <span className="cms-editor-divider" />
        <ToolbarButton
          title="Bullet list"
          icon="list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          title="Numbered list"
          icon="hash"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          title="Code block"
          icon="code"
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        />
        <span className="cms-editor-divider" />
        <ToolbarButton title="Link" icon="link" active={editor.isActive("link")} onClick={setLink} />
        <ToolbarButton title="Image" icon="image" onClick={handleImagePick} />
        <ToolbarButton title="Insert button" icon="square" onClick={insertButton} />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="cms-editor-hidden-input"
          onChange={handleImageFile}
        />
      </div>
      <EditorContent editor={editor} className="cms-editor-content" />
    </div>
  );
};

export default BlogEditor;
